import { feedbackAudioUrl, isCorrectFeedback } from '../feedbackAudio';
import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';
import TouchAppRoundedIcon from '@mui/icons-material/TouchAppRounded';
import EastRoundedIcon from '@mui/icons-material/EastRounded';
import MiniTaskGameHeader from '../components/MiniTaskGameHeader';
import { GardenBackdrop } from '../components/RoundView';
import { withTurkishGenitive } from '../turkishSuffixes';
import Grade1Letter from './Grade1Letter';
import FlipCard from '../components/FlipCard';
import TrainEngineArt from './TrainEngineArt';
import useTourIdleHelp from './useTourIdleHelp';
import { setRoundAudioPlayback } from './pronunciationPlayback';
import './soundHunt.css';
import './memoryTrain.css';
import './l4Tour3.css';
import './premiumTrain.css';
import './studentTrainVisualParity.css';

const Glyph = Grade1Letter;
const emptySlots = (item) => Array(item.target_sequence?.length || 2).fill(null);

export default function MemoryTrainRoundView({ round, classTitle, onExit, onNextRound, onComplete,
  sharedAudioRef, localProgress, idleHelpDisabled = false }) {
  const memory = round.assessment_mode === 'letter_memory';
  const isL4G06 = round.source_level === 4 && round.game_id === 'G06' && memory;
  const wordBuild = round.assessment_mode === 'word_construction';
  const physical = !!round.physical_stones;
  const premiumTrain = round.assessment_mode === 'syllable_train' && !physical;
  const [phase, setPhase] = useState('waiting');
  const [index, setIndex] = useState(0);
  const [opened, setOpened] = useState([]);
  const [matched, setMatched] = useState([]);
  const [pairAttempts, setPairAttempts] = useState(0);
  const [slots, setSlots] = useState(() => emptySlots(round.items[0]));
  const [departing, setDeparting] = useState(false);
  const [dragOffset, setDragOffset] = useState(null);
  const [error, setError] = useState('');
  const handlers = useRef({});
  const ownAudio = useRef(null);
  const audioRef = sharedAudioRef || ownAudio;
  const drag = useRef(null);
  const suppressClick = useRef(false);
  const completion = useRef(onComplete);
  completion.current = onComplete;
  useTourIdleHelp(phase === 'ready' && !dragOffset && !idleHelpDisabled && (!memory || round.unmeasured_help || pairAttempts >= 2), () => handlers.current.help(),
    memory ? opened.join(',') : slots.join(','));

  useEffect(() => {
    let alive = true, locked = true, running = false, cursor = 0, retry, cancelAudio, cancelDelay;
    let events = [], faceUp = [], pairs = [], placed = emptySlots(round.items[0]), attempts = 0, helpShown = false;
    const report = () => localProgress(round.game_id, events);
    const playOnce = (id) => new Promise((resolve, reject) => {
      const url = feedbackAudioUrl(id, round.audio[id]);
      if (!alive || !url) { reject(new Error('Ses kaydı bulunamadı.')); return; }
      const audio = audioRef.current || new Audio(url);
      audioRef.current = audio;
      audio.src = url; audio.preload = 'auto'; audio.muted = false;
      setRoundAudioPlayback(audio, round, id);
      let timer, settled = false;
      const clean = () => { clearTimeout(timer); audio.onended = null; audio.onerror = null; cancelAudio = null; };
      const fail = (err) => { if (settled) return; settled = true; clean(); audio.pause(); reject(err); };
      cancelAudio = () => fail(new Error('cancelled'));
      audio.onended = () => { if (settled) return; settled = true; clean(); resolve(); };
      audio.onerror = () => fail(new Error('Ses oynatılamadı. Tekrar deneyelim.'));
      timer = setTimeout(() => fail(new Error('Ses yüklenemedi. Tekrar deneyelim.')), isCorrectFeedback(id) ? 1500 : 30000);
      try { Promise.resolve(audio.play()).catch(fail); } catch (err) { fail(err); }
    });
    const play = (id) => playOnce(id).catch((err) => {
      if (!alive) throw err;
      if (isCorrectFeedback(id)) return;
      return playOnce(id);
    });
    const speech = (ids) => {
      let position = 0;
      return async () => { while (alive && position < ids.length) { await play(ids[position]); position += 1; } };
    };
    const delay = (ms) => new Promise((resolve) => {
      const timer = setTimeout(() => { cancelDelay = null; resolve(); }, ms);
      cancelDelay = () => { clearTimeout(timer); resolve(); };
    });
    const ready = () => { if (alive) { locked = false; setPhase('ready'); } };
    const run = async (operation) => {
      if (!alive || running) return;
      locked = true; running = true; setError(''); setPhase('playing');
      retry = () => run(operation);
      try { await operation(); }
      catch (err) {
        if (alive) { setError(err.name === 'NotAllowedError' ? 'Ses başlayamadı. Tekrar Dene düğmesine dokun.' : err.message); setPhase('retry'); }
      } finally { running = false; }
    };
    const targetAudio = () => memory ? [] : [round.items[cursor].audio_id];
    const intro = speech([round.intro_audio_id, ...targetAudio()]);
    const start = () => run(async () => { await intro(); if (alive) { report(); ready(); } });
    const replay = (instruction = false) => {
      if (locked) return;
      const sequence = speech(instruction || memory ? [round.intro_audio_id, ...targetAudio()] : targetAudio());
      run(async () => { await sequence(); ready(); });
    };
    const help = () => {
      if (locked || helpShown) return;
      helpShown = true;
      if (!round.unmeasured_help) events = [...events, { kind: 'help_prompt' }];
      report();
      const sequence = speech([round.help_audio_id, ...targetAudio()]);
      run(async () => { await sequence(); ready(); });
    };
    const finish = () => {
      if (!alive) return;
      if (round.next_round && onNextRound) onNextRound(round.next_round);
      else completion.current();
    };
    const flip = (id) => {
      if (locked || pairs.includes(id) || faceUp.includes(id)) return;
      const item = round.items.find((card) => card.id === id);
      if (!item) return;
      events = [...events, { kind: 'select', item_id: id }];
      faceUp = [...faceUp, id]; setOpened(faceUp);
      const result = report();
      if (faceUp.length < 2) return;
      locked = true; attempts += 1; setPairAttempts(attempts);
      const equal = round.items.find((card) => card.id === faceUp[0]).letter === item.letter;
      if (!equal) {
        setPhase('revealing');
        delay(850).then(() => { if (alive) { faceUp = []; setOpened([]); ready(); } });
        return;
      }
      pairs = [...pairs, ...faceUp]; setMatched(pairs);
      const pop = speech(['s_l1_g2_pop']);
      const complete = speech(round.skip_completion_speech ? [] : [round.complete_audio_id]);
      run(async () => {
        await pop();
        if (!alive) return;
        faceUp = []; setOpened([]);
        if (result.round_completed) { setPhase('finishing'); await complete(); finish(); }
        else ready();
      });
    };
    const place = (letter, position = null) => {
      const item = round.items[cursor];
      if (locked || !(physical ? item.stones.some((stone) => stone.id === letter) : item.target_sequence.includes(letter)) || placed.includes(letter)) return;
      const next = position ?? placed.findIndex((value) => !value);
      if (next < 0 || next >= placed.length || placed[next]) return;
      placed = placed.map((value, index) => index === next ? letter : value); setSlots(placed);
      if (placed.some((value) => !value)) return;
      // Only a full placement is an assessment. Physical IDs distinguish repeated letters.
      const sequenceId = placed.join(physical ? ',' : '');
      events = [...events, { kind: 'select', item_id: item.id, selected_id: sequenceId }];
      const result = report();
      locked = true;
      const text = physical ? placed.map((id) => item.stones.find((stone) => stone.id === id).letter).join('') : sequenceId;
      const correct = text === item.target_sequence.join('');
      const feedback = speech(correct ? ['s_l1_g2_pop'] : [round.wrong_audio_id, ...targetAudio()]);
      const complete = speech(round.skip_completion_speech ? [] : [round.complete_audio_id]);
      const nextSpeech = speech(cursor + 1 < round.items.length ? [round.items[cursor + 1].audio_id] : []);
      let animated = false, advanced = false;
      if (!correct) { placed = Array(item.target_sequence.length).fill(null); setSlots(placed); }
      run(async () => {
        await feedback();
        if (!alive) return;
        if (!correct) { ready(); return; }
        setPhase('finishing');
        if (!animated) { setDeparting(true); await delay(1100); animated = true; }
        if (!alive) return;
        if (result.round_completed) { await complete(); finish(); return; }
        if (!advanced) {
          cursor += 1; advanced = true; placed = Array(round.items[cursor].target_sequence.length).fill(null);
          setIndex(cursor); setSlots(placed); setDeparting(false);
        }
        await nextSpeech(); ready();
      });
    };
    const empty = (position) => {
      if (locked || position < 0 || position >= placed.length) return;
      placed = placed.map((value, index) => index === position ? null : value); setSlots(placed);
    };
    handlers.current = { start, replay, help, flip, place, empty, retry: () => retry?.() };
    if (round.continuing) start();
    return () => { alive = false; locked = true; cancelAudio?.(); cancelDelay?.(); audioRef.current?.pause(); };
  }, [round, memory, physical, audioRef, localProgress, onNextRound]);

  const pointerDown = (event, letter) => {
    if (phase !== 'ready' || slots.includes(letter)) return;
    suppressClick.current = false;
    drag.current = { id: event.pointerId, x: event.clientX, y: event.clientY, letter, moved: false };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const pointerMove = (event) => {
    const current = drag.current;
    if (!current || current.id !== event.pointerId) return;
    const x = event.clientX - current.x, y = event.clientY - current.y;
    if (Math.hypot(x, y) > 8) current.moved = true;
    if (current.moved) setDragOffset({ x, y, letter: current.letter });
  };
  const pointerUp = (event) => {
    const current = drag.current;
    if (!current || current.id !== event.pointerId) return;
    drag.current = null; setDragOffset(null);
    if (current.moved) {
      suppressClick.current = true;
      const target = document.elementFromPoint(event.clientX, event.clientY)?.closest('[data-train-slot]');
      if (target) handlers.current.place(current.letter, Number(target.dataset.trainSlot));
    }
  };
  const pairCount = round.pair_count || round.items.length / 2;
  const choices = physical ? round.items[index].stones : round.choice_orders ? round.choice_orders[index].map((id) => ({ id, letter: id })) : round.choices;
  const stoneLetter = (id) => physical ? choices.find((stone) => stone.id === id)?.letter : id;
  return <Box className={`sound-hunt memory-train ${isL4G06 ? 'l4-tour3 l4-g06' : ''} ${memory ? 'letter-memory' : 'syllable-train'} ${premiumTrain ? 'hece-premium' : ''} ${physical ? 'physical-construction' : ''} ${memory && round.items.length === 8 ? 'has-eight-cards' : ''}`} style={{ '--train-slot-count': slots.length }}>
    {!premiumTrain && <GardenBackdrop />}
    <Box className="sound-hunt-content">
      <MiniTaskGameHeader classTitle={classTitle} soundEnabled soundRequired />
      <Box className="sound-hunt-header">
        <Button className="sound-hunt-home" onClick={onExit} startIcon={<ArrowBackRoundedIcon />}>Ana sayfa</Button>
        <Typography component="h1" className="sound-hunt-title">{withTurkishGenitive(round.student?.display_name || 'Öğrenci')} Mini Görevi</Typography>
        <Box className="sound-hunt-progress" aria-label={round.progress_text ? `Meydan okuma ${round.progress_text}` : memory ? `Bulunan eş ${matched.length / 2} / ${pairCount}` : round.items.length > 1 ? `${wordBuild ? 'Kelime' : 'Hece'} ${index + 1} / ${round.items.length}` : 'İki vagon'}>
          {round.progress_text || (memory ? `${matched.length / 2} / ${pairCount}` : round.items.length > 1 ? `${index + 1} / ${round.items.length}` : <span className="train-progress-dots" aria-hidden="true">{slots.map((letter, index) => <i key={index} className={letter ? 'is-filled' : ''} />)}</span>)}
        </Box>
      </Box>
      <Box className="sound-hunt-bottom">
        <Typography className="sound-hunt-badge">{round.badge_title || (memory ? '🃏 Harf Hafızası' : '🚂 Hece Treni')}</Typography>
        <Box className="practice-cue" aria-hidden="true">
          {memory ? <><TouchAppRoundedIcon /><span>İki kart aç, eşlerini bul!</span></>
            : <><VolumeUpRoundedIcon /><span>Dinle</span><EastRoundedIcon /><TouchAppRoundedIcon /><span>Harfleri vagonlara taşı</span></>}
        </Box>
        {memory ? <Box className="letter-memory-cards">
          {round.items.map((item, index) => {
            const showing = opened.includes(item.id) || matched.includes(item.id);
            const found = matched.includes(item.id);
            return <FlipCard key={item.id} open={showing} matched={found}
              aria-label={found ? `${index + 1}. kart, eş bulundu` : showing ? `${index + 1}. kart, ${item.letter}, açık` : `${index + 1}. kartı aç`}
              disabled={phase !== 'ready'} className="letter-memory-card"
              onClick={() => handlers.current.flip(item.id)}
              back={<><span>✦</span><TouchAppRoundedIcon /></>}
              front={<><Glyph letter={item.letter} />{found && <i>✓</i>}</>} />;
          })}
        </Box> : <>
          {round.items[index].image && <img className="choice-reference-image" src={round.items[index].image.url} alt="" />}
          <Button className="train-listen" disabled={phase !== 'ready'} aria-label={wordBuild ? 'Kelimeyi yeniden dinle' : 'Heceyi yeniden dinle'} onClick={() => handlers.current.replay()}><VolumeUpRoundedIcon /></Button>
          <Box className={`train-scene ${departing ? 'is-departing' : ''}`}>
            <Box className="train-track" aria-hidden="true" />
            <Box className="train-carriages">
              {slots.map((letter, index) => <button key={index} type="button" data-train-slot={index}
                aria-label={letter ? `${index + 1}. vagonu boşalt` : `${index + 1}. boş vagon`}
                disabled={phase !== 'ready'} onClick={() => handlers.current.empty(index)}
                className={`train-wagon ${letter ? 'is-filled' : ''} ${index === slots.findIndex((value) => !value) ? 'is-next' : ''}`}>
                <span className="train-window">{letter ? <Glyph letter={stoneLetter(letter)} /> : <><span className="wagon-number" aria-hidden="true">{index + 1}</span><TouchAppRoundedIcon /></>}</span>
                <span className="wagon-wheels" aria-hidden="true"><i /><i /></span>
              </button>)}
              <TrainEngineArt premium={premiumTrain} />
            </Box>
          </Box>
          <Box className="train-stone-cue" aria-hidden="true"><TouchAppRoundedIcon /> Dokun veya taşı</Box>
          <Box className="train-stones" data-stone-count={choices.length}>
            {choices.map(({ id: letter }) => <button key={letter} type="button" aria-label={`${stoneLetter(letter)} harfini yerleştir`}
              disabled={phase !== 'ready' || slots.includes(letter)}
              className={`train-stone ${slots.includes(letter) ? 'is-used' : ''} ${dragOffset?.letter === letter ? 'is-dragging' : ''}`}
              style={dragOffset?.letter === letter ? { transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)` } : undefined}
              onPointerDown={(event) => pointerDown(event, letter)} onPointerMove={pointerMove} onPointerUp={pointerUp}
              onPointerCancel={() => { drag.current = null; setDragOffset(null); }}
              onClick={(event) => {
                const suppressed = suppressClick.current && event.detail > 0;
                suppressClick.current = false;
                if (!suppressed) handlers.current.place(letter);
              }}><Glyph letter={stoneLetter(letter)} /></button>)}
          </Box>
        </>}
        <Box className="sound-hunt-controls">
          <Button className="sound-hunt-control" disabled={!['waiting', 'ready', 'retry'].includes(phase)} startIcon={<RecordVoiceOverRoundedIcon />}
            onClick={() => phase === 'waiting' ? handlers.current.start() : phase === 'retry' ? handlers.current.retry() : handlers.current.replay(true)}>
            {phase === 'retry' ? 'Tekrar Dene' : 'Yönergeyi dinleyelim'}
          </Button>
        </Box>
        <Typography role="status" className="sound-hunt-status">{error}</Typography>
      </Box>
    </Box>
  </Box>;
}
